// The Hub's documentation lives on the specs site's help center, the one
// reference for every Hub app (web, iOS, Android, macOS, Windows, Linux):
// https://compositecode.github.io/substratalapps-applications-specs/help/
//
// Every screen has a page there at help/screens/<screen-id>.html, generated
// from its specification, which is what each screen's "?" opens.
export const HELP_BASE = "https://compositecode.github.io/substratalapps-applications-specs/help/";

export function helpHome(): string {
  return HELP_BASE;
}

/** The help page for a screen in the Hub spec, such as USR-01. */
export function helpForScreen(screenId: string): string {
  return `${HELP_BASE}screens/${screenId.toLowerCase()}.html`;
}

export function helpPage(path: string): string {
  return `${HELP_BASE}${path.replace(/^\//, "")}`;
}

interface IndexEntry {
  /** title */ t: string;
  /** URL, relative to HELP_BASE */ u: string;
  /** kind, such as Guides, Screens or API v1 */ k: string;
  /** summary */ s: string;
  /** extra searchable text */ x: string;
}

export interface HelpHit {
  title: string;
  url: string;
  kind: string;
}

let index: Promise<IndexEntry[]> | null = null;

function load(): Promise<IndexEntry[]> {
  index ??= fetch(`${HELP_BASE}search-index.json`)
    .then((r) => {
      if (!r.ok) throw new Error(`help search index: ${r.status}`);
      return r.json() as Promise<IndexEntry[]>;
    })
    .catch((err) => {
      index = null;
      throw err;
    });
  return index;
}

/**
 * Searches the help center's index (fetched once, cross-origin; GitHub Pages
 * allows it). Every word must match; titles rank above summaries above body text.
 */
export async function searchHelp(query: string, limit = 8): Promise<HelpHit[]> {
  const words = query.toLowerCase().split(/\s+/).filter((w) => w.length >= 2);
  if (!words.length) return [];
  const entries = await load();
  return entries
    .map((e) => {
      const t = e.t.toLowerCase();
      const s = e.s.toLowerCase();
      const x = e.x.toLowerCase();
      let score = 0;
      for (const w of words) {
        if (t.includes(w)) score += 10;
        else if (s.includes(w)) score += 3;
        else if (x.includes(w)) score += 1;
        else return null;
      }
      return { score, hit: { title: e.t, url: HELP_BASE + e.u, kind: e.k } };
    })
    .filter((r): r is { score: number; hit: HelpHit } => r !== null)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map((r) => r.hit);
}
